import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Loader2, AlertTriangle, X } from 'lucide-react'
import { LpLayout } from './components/Layout'
import { SalesLinkService, type SalesLinkPayload, type SalesLinkPlan } from '@/services/modules/SalesLinkService'
import { useSalesLinkStore } from './store'

/** Formata um telefone brasileiro: (00) 00000-0000 / (00) 0000-0000. */
function formatPhoneBR(raw: string | null | undefined): string {
  const d = (raw ?? '').replace(/\D+/g, '').replace(/^55(?=\d{10,11}$)/, '')
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`
  return raw ?? ''
}

/**
 * /vendas/:slug — réplica pixel-perfect da LP de planos do site referência
 * (amplamaisbeneficios.com.br/planos/contrate/:slug), construída a partir
 * do DOM pós-render (lp-chunks/*.html).
 *
 * Estrutura:
 *   <main class="bg-gray-100 px-8">
 *     <div class="max-w-[1440px] mx-auto">     // (8xl pedido pelo usuário)
 *       <header>...</header>
 *       <section class="bg-white rounded-lg p-6">  // hero
 *       <section class="pt-12 pb-6 px-6 bg-white"> // grid + CTA empresa
 *       <footer class="mt-12 mb-6 p-4 bg-[#243C4F] rounded-lg">
 *     </div>
 *   </main>
 */
export function SalesLinkPlansPage() {
  const { slug = '' } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const setPayload = useSalesLinkStore((s) => s.setPayload)
  const [data, setData] = useState<SalesLinkPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [benefitsModalPlan, setBenefitsModalPlan] = useState<SalesLinkPlan | null>(null)

  useEffect(() => {
    if (!slug) return
    setLoading(true)
    SalesLinkService.show(slug)
      .then((d) => {
        setData(d)
        setPayload(d)
      })
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true)
      })
      .finally(() => setLoading(false))
  }, [slug, setPayload])

  const plans = useMemo(() => {
    if (!data) return []
    return [...data.plans]
      .filter((p) => p.status === 'ATIVO')
      .sort((a, b) => {
        const ax = a.lpOrder ?? 999
        const bx = b.lpOrder ?? 999
        if (ax !== bx) return ax - bx
        return a.price - b.price
      })
  }, [data])

  if (loading) return <FullScreenSpinner />
  if (notFound || !data) return <NotFoundScreen />

  return (
    <LpLayout>
      <main className="bg-gray-100 px-4 sm:px-8 min-h-dvh">
        <div className="max-w-[1440px] mx-auto">

          {/* ===== Header ===== */}
          <div className="pt-12 pb-6">
            <div className="w-full flex items-center justify-between">
              <a
                href="https://amplamaisbeneficios.com.br"
                className="cursor-pointer flex items-center gap-2 hover:underline"
              >
                <img alt="Ampla+ Benefícios" className="h-24" src="/logo-light-full.png" />
              </a>
              <a
                href="https://amplamaisbeneficios.com.br"
                className="cursor-pointer flex items-center justify-center gap-2 rounded-lg bg-white text-[#56758D] px-4 py-4 text-[14px] font-[500] hover:bg-gray-200 w-[144px]"
              >
                <IconBackArrow />
                <span className="cursor-pointer">VOLTAR</span>
              </a>
            </div>
          </div>

          {/* ===== Hero (texto + corretor card) ===== */}
          <section className="bg-white rounded-lg p-6">
            <div className="flex flex-col md:flex-row gap-6">
              <div className="w-full md:w-1/2 flex items-center">
                <h1 className="text-[24px] sm:text-[32px] font-[500] text-[#243c4f] leading-tight">
                  Escolha o seu plano com as{' '}
                  <span className="text-[#f07d00] font-[700]">condições especiais</span>{' '}
                  do nosso corretor parceiro!
                </h1>
              </div>
              <div className="w-full md:w-1/2 flex items-center">
                <ConsultantCard seller={data.seller} />
              </div>
            </div>
          </section>

          {/* ===== Grid de planos + CTA empresa ===== */}
          <section className="pt-12 pb-6 px-4 sm:px-6 bg-white rounded-lg mt-6">
            {plans.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 p-8 text-center text-[#56758d]">
                Nenhum plano disponível no momento. Entre em contato com {data.seller.name}.
              </div>
            ) : (
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                {plans.map((p) => (
                  <PlanCard
                    key={p.id}
                    plan={p}
                    onSelect={() => navigate(`/vendas/${slug}/${p.id}`)}
                    onOpenBenefits={() => setBenefitsModalPlan(p)}
                  />
                ))}
              </div>
            )}

            {/* CTA empresa */}
            <div className="mt-8">
              <div
                className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm relative overflow-hidden"
                style={{
                  backgroundImage: "linear-gradient(135deg, #243C4F 0%, #1a2d3e 100%)",
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                  backgroundSize: 'cover',
                }}
              >
                <div className="relative z-10 flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
                  <div>
                    <h3 className="text-[22px] sm:text-[28px] font-[500] text-white leading-tight">
                      Precisando de um plano para{' '}
                      <span className="font-[700] text-[#f07d00]">sua empresa?</span>
                    </h3>
                    <p className="mt-1 text-white text-[16px] font-[400]">
                      Entre em contato com nosso time de vendas e conheça nossas opções de planos a partir de 2 vidas.
                    </p>
                  </div>
                  {data.seller.whatsapp && (
                    <a
                      href={`https://api.whatsapp.com/send?phone=55${data.seller.whatsapp.replace(/\D+/g, '')}&text=${encodeURIComponent(
                        `Olá ${data.seller.name}, gostaria de saber sobre planos para minha empresa.`,
                      )}`}
                      target="_blank"
                      rel="noreferrer"
                      className="cursor-pointer flex gap-2 bg-[#f07d00] text-white no-underline items-center h-12 px-3 rounded-lg min-w-[304px] justify-center hover:bg-[#e67300] transition"
                    >
                      <IconWhatsApp white />
                      CHAMAR NO WHATSAPP
                    </a>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* ===== Footer ===== */}
          <footer className="mt-12 mb-10 p-4 bg-[#243C4F] rounded-lg">
            <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
              <p className="text-white text-[14px]">
                © {new Date().getFullYear()} Ampla Mais Benefícios - Todos os direitos reservados
              </p>
              <div className="flex items-center gap-2">
                <p className="text-white me-4 text-[14px]">Siga nossas redes sociais</p>
                <a href="https://www.facebook.com" target="_blank" rel="noreferrer" aria-label="Facebook" className="cursor-pointer hover:opacity-90">
                  <SocialTile><IconFacebook /></SocialTile>
                </a>
                <a href="https://www.instagram.com" target="_blank" rel="noreferrer" aria-label="Instagram" className="cursor-pointer hover:opacity-90">
                  <SocialTile><IconInstagram /></SocialTile>
                </a>
                <a href="https://linkedin.com" target="_blank" rel="noreferrer" aria-label="LinkedIn" className="cursor-pointer hover:opacity-90">
                  <SocialTile><IconLinkedIn /></SocialTile>
                </a>
              </div>
            </div>
          </footer>
        </div>
      </main>

      {/* Modal de benefícios (radix-style dialog) */}
      <BenefitsModal plan={benefitsModalPlan} onClose={() => setBenefitsModalPlan(null)} />
    </LpLayout>
  )
}

// =============================================================================
// ConsultantCard — réplica do bloco bg-gray-100 com chip "Corretor Parceiro"
// =============================================================================
function ConsultantCard({ seller }: { seller: SalesLinkPayload['seller'] }) {
  return (
    <div className="w-full bg-gray-100 rounded-lg p-4">
      <div className="flex flex-col gap-6 md:flex-row md:items-center">
        <div className="w-full md:w-1/2 min-w-0 flex flex-col gap-4">
          <span className="inline-flex items-center justify-center gap-2 rounded-sm bg-white p-1 self-start">
            <IconHandshake />
            <span className="text-[#56758d] text-[14px]">Corretor Parceiro</span>
          </span>
          <h2 className="text-[#243c4f] text-[18px] sm:text-[20px] md:text-[22px] font-[600] leading-snug break-words">
            {seller.name}
          </h2>
        </div>
        <div className="w-full md:w-1/2 min-w-0 flex flex-col gap-2">
          {seller.whatsapp && (
            <span className="text-[#56758d] text-[14px] sm:text-[16px] font-[400] flex items-center gap-2">
              <IconWhatsApp />
              <a
                href={`https://wa.me/55${seller.whatsapp.replace(/\D+/g, '')}`}
                target="_blank"
                rel="noreferrer"
                className="cursor-pointer hover:underline"
              >
                {formatPhoneBR(seller.whatsapp)}
              </a>
            </span>
          )}
          {seller.email && (
            <span className="text-[#56758d] text-[14px] sm:text-[16px] font-[400] flex items-start gap-2">
              <span className="mt-0.5 shrink-0"><IconMail /></span>
              <a href={`mailto:${seller.email}`} className="cursor-pointer hover:underline break-all">
                {seller.email}
              </a>
            </span>
          )}
          {(seller.city || seller.uf) && (
            <span className="text-[#56758d] text-[14px] sm:text-[16px] font-[400] flex items-center gap-2">
              <IconLocation />
              {[seller.city, seller.uf].filter(Boolean).join(' / ')}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// PlanCard
// =============================================================================
function PlanCard({
  plan, onSelect, onOpenBenefits,
}: {
  plan: SalesLinkPlan
  onSelect: () => void
  onOpenBenefits: () => void
}) {
  // Apenas dados reais cadastrados (sem inventar). Quando não houver, o item
  // simplesmente não aparece (nada de foto/benefício "fake").
  const displayName = plan.lpName?.trim() || plan.name
  const coverage    = plan.lpCoverage?.trim() || null
  const audience    = plan.lpHolder?.trim()   || deriveAudience(plan)
  const finePrint   = plan.lpNotes?.trim()    || null
  const imageUrl    = plan.lpImageUrl
  const hasBenefits = (plan.lpBenefits?.length ?? 0) > 0

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm flex flex-col h-full">
      {/* Topo: capa real ou placeholder de marca (sem foto stock) */}
      {imageUrl ? (
        <img
          alt={displayName}
          className="w-full h-40 object-cover rounded-lg"
          src={imageUrl}
          onError={(e) => { (e.target as HTMLImageElement).style.visibility = 'hidden' }}
        />
      ) : (
        <div className="w-full h-40 rounded-lg bg-gradient-to-br from-[#046fb7] to-[#243c4f] flex items-center justify-center px-4">
          <span className="text-white/95 text-[18px] font-[700] uppercase text-center leading-tight">{displayName}</span>
        </div>
      )}
      <p className="text-[#f07d00] text-[16px] font-[600] mb-0 mt-3">PLANO</p>
      <h3 className="text-[#243c4f] text-[22px] sm:text-[24px] font-[700] uppercase mt-1 leading-tight">
        {displayName}
      </h3>

      {/* Descrição variável — absorve a diferença de altura entre cards */}
      <div className="mt-3 flex flex-col gap-2 flex-1">
        {coverage && (
          <div className="flex items-start gap-2">
            <IconCheckCircle />
            <span className="text-[#56758d] text-[14px] font-[400] whitespace-pre-line">{coverage}</span>
          </div>
        )}
        <div className="flex items-start gap-2">
          <IconAvatar />
          <span className="text-[#56758d] text-[14px] font-[400] whitespace-pre-line">{audience}</span>
        </div>
        {finePrint && (
          <p className="text-[12px] text-[#56758d] whitespace-pre-line">{finePrint}</p>
        )}
      </div>

      {/* Rodapé fixo (alinhado entre todos os cards) */}
      <div className="mt-4">
        {hasBenefits && (
          <button
            type="button"
            onClick={onOpenBenefits}
            className="cursor-pointer w-full flex items-center justify-center border border-gray-200 gap-2 rounded-md px-1 py-3 text-[14px] font-[400] hover:bg-gray-100 transition"
          >
            <IconPlus />
            <span className="text-[#243c4f] cursor-pointer">Benefícios do plano</span>
          </button>
        )}

        <div className="pt-4 flex items-baseline gap-2">
          <div className="text-[#243c4f] font-[700] text-[26px] sm:text-[28px]">
            R$ {plan.price.toFixed(2).replace('.', ',')}
          </div>
          <span className="text-[#f07d00] font-[700] text-[14px]">/MÊS</span>
        </div>

        <button
          onClick={onSelect}
          className="cursor-pointer mt-4 bg-[#046fb7] hover:bg-[#56758d] text-white rounded-md text-[14px] font-[500] px-2 py-3 w-full transition"
        >
          CONTRATAR
        </button>
      </div>
    </div>
  )
}

// =============================================================================
// BenefitsModal — dialog Radix-style com overlay e fade-in
// =============================================================================
function BenefitsModal({ plan, onClose }: { plan: SalesLinkPlan | null; onClose: () => void }) {
  useEffect(() => {
    if (!plan) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [plan, onClose])

  if (!plan) return null

  const benefits = plan.lpBenefits ?? []

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="lp-root fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-[#243C4FE6] bg-opacity-95"
      onClick={onClose}
    >
      <div
        className="lp-fade-in w-full sm:max-w-lg bg-white sm:rounded-2xl rounded-t-2xl shadow-xl ring-1 ring-gray-200 max-h-[92dvh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 px-5 sm:px-6 py-4 border-b border-gray-100 shrink-0">
          <div className="min-w-0">
            <p className="text-[#f07d00] text-[14px] font-[600]">PLANO</p>
            <h3 className="text-[#243c4f] text-[20px] font-[700] uppercase">{plan.name}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer p-2 -mr-1 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600"
            aria-label="Fechar"
          >
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-5">
          <ul className="space-y-3">
            {benefits.map((b) => (
              <li key={b} className="flex items-start gap-3 text-[#56758d] text-[14px]">
                <span className="shrink-0 mt-0.5"><IconCheckCircle /></span>
                <span className="text-[#243c4f]">{b}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="px-5 sm:px-6 py-4 border-t border-gray-100 bg-gray-50/60 flex items-baseline justify-between gap-3 shrink-0">
          <div className="flex items-baseline gap-2">
            <span className="text-[#56758d] text-[12px]">Valor do plano</span>
            <span className="text-[#243c4f] font-[700] text-[22px]">
              R$ {plan.price.toFixed(2).replace('.', ',')}
            </span>
            <span className="text-[#f07d00] font-[700] text-[12px]">/MÊS</span>
          </div>
          <button
            onClick={onClose}
            className="cursor-pointer bg-[#046fb7] hover:bg-[#56758d] text-white rounded-md text-[14px] font-[500] px-4 py-2 transition"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// SVG icons (inline) — réplicas dos assets do site referência
// =============================================================================
function IconBackArrow() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="cursor-pointer" aria-hidden="true">
      <path d="M7.58118 7.50195L11.5656 3.51758C11.8409 3.24219 12.2863 3.24219 12.5587 3.51758L13.2208 4.17969C13.4962 4.45508 13.4962 4.90039 13.2208 5.17285L10.3995 8L13.2238 10.8242C13.4991 11.0996 13.4991 11.5449 13.2238 11.8174L12.5616 12.4824C12.2863 12.7578 11.8409 12.7578 11.5685 12.4824L7.58411 8.49805C7.30579 8.22266 7.30579 7.77734 7.58118 7.50195ZM1.95618 8.49805L5.94055 12.4824C6.21594 12.7578 6.66126 12.7578 6.93372 12.4824L7.59583 11.8203C7.87122 11.5449 7.87122 11.0996 7.59583 10.8271L4.77454 8L7.59876 5.17578C7.87415 4.90039 7.87415 4.45508 7.59876 4.18262L6.93665 3.51758C6.66126 3.24219 6.21594 3.24219 5.94348 3.51758L1.95911 7.50195C1.68079 7.77734 1.68079 8.22266 1.95618 8.49805Z" fill="#56758D"/>
    </svg>
  )
}
function IconHandshake() {
  return (
    <svg width="20" height="21" viewBox="0 0 20 21" fill="none" className="h-5 w-5" aria-hidden="true">
      <path d="M15.25 8.01998H10.5V9.76998C10.5 11.0106 9.49062 12.02 8.25 12.02C7.00937 12.02 6 11.0106 6 9.76998V5.96998L3.97187 7.18873C3.36875 7.54811 3 8.20123 3 8.90123V10.3794L0.499996 11.8231C0.0218706 12.0981 -0.143754 12.7106 0.134371 13.1887L2.63437 17.52C2.90937 17.9981 3.52187 18.1606 4 17.8856L7.23125 16.02H11.5C12.6031 16.02 13.5 15.1231 13.5 14.02H14C14.5531 14.02 15 13.5731 15 13.02V11.02H15.25C15.6656 11.02 16 10.6856 16 10.27V8.76998C16 8.35436 15.6656 8.01998 15.25 8.01998ZM19.8656 6.85123L17.3656 2.51998C17.0906 2.04186 16.4781 1.87936 16 2.15436L12.7687 4.01998H9.575C9.2 4.01998 8.83437 4.12623 8.51562 4.32311L7.46875 4.97623C7.175 5.15748 7 5.47936 7 5.82311V9.76998C7 10.4606 7.55937 11.02 8.25 11.02C8.94062 11.02 9.5 10.4606 9.5 9.76998V7.01998H15.25C16.2156 7.01998 17 7.80436 17 8.76998V9.66061L19.5 8.21686C19.9781 7.93873 20.1406 7.32936 19.8656 6.85123Z" fill="#F07D00"/>
    </svg>
  )
}
function IconWhatsApp({ white = false }: { white?: boolean }) {
  const fill = white ? '#FFFFFF' : '#F07D00'
  const cls = white ? 'h-6 w-6' : 'h-4 w-4'
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" className={cls} aria-hidden="true">
      <path d="M11.6802 2.67507C10.5345 1.52663 9.00876 0.894989 7.38728 0.894989C4.04041 0.894989 1.31697 3.61843 1.31697 6.9653C1.31697 8.03444 1.59587 9.07897 2.12634 10.0005L1.26501 13.145L4.48337 12.3001C5.36931 12.7841 6.36736 13.0383 7.38455 13.0383H7.38728C10.7314 13.0383 13.515 10.3149 13.515 6.96804C13.515 5.34655 12.826 3.8235 11.6802 2.67507ZM7.38728 12.0157C6.47947 12.0157 5.5908 11.7723 4.81697 11.313L4.63376 11.2036L2.72517 11.704L3.23376 9.84186L3.11345 9.65046C2.60759 8.84655 2.34236 7.9196 2.34236 6.9653C2.34236 4.18444 4.60642 1.92038 7.39001 1.92038C8.73806 1.92038 10.0041 2.44538 10.9556 3.39968C11.9072 4.35397 12.4924 5.61999 12.4896 6.96804C12.4896 9.75163 10.1681 12.0157 7.38728 12.0157ZM10.1545 8.23679C10.0041 8.16022 9.25759 7.79382 9.11814 7.7446C8.97869 7.69265 8.87751 7.66804 8.77634 7.82116C8.67517 7.97429 8.38533 8.31335 8.29509 8.41725C8.20759 8.51843 8.11736 8.5321 7.96697 8.45554C7.07556 8.00983 6.49041 7.65983 5.90251 6.65085C5.74666 6.38288 6.05837 6.40202 6.34822 5.82233C6.39744 5.72116 6.37283 5.63366 6.33455 5.5571C6.29626 5.48054 5.99275 4.73405 5.86697 4.43054C5.74392 4.13522 5.61814 4.17624 5.52517 4.17077C5.43767 4.1653 5.3365 4.1653 5.23533 4.1653C5.13416 4.1653 4.97009 4.20358 4.83064 4.35397C4.69119 4.5071 4.30017 4.8735 4.30017 5.61999C4.30017 6.36647 4.84431 7.08835 4.91814 7.18952C4.9947 7.29069 5.98728 8.82194 7.51033 9.48093C8.47283 9.89655 8.85017 9.9321 9.33142 9.861C9.624 9.81725 10.2283 9.4946 10.3541 9.13913C10.4799 8.78366 10.4799 8.48015 10.4416 8.41725C10.406 8.3489 10.3049 8.31061 10.1545 8.23679Z" fill={fill}/>
    </svg>
  )
}
function IconMail() {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" className="h-4 w-4" aria-hidden="true">
      <path d="M7.39001 0.238739C3.64231 0.238739 0.608765 3.27168 0.608765 7.01999C0.608765 10.7677 3.64171 13.8012 7.39001 13.8012C8.70673 13.8012 9.99702 13.4146 11.0926 12.7014C11.4208 12.4877 11.4925 12.0373 11.2444 11.7342L10.9661 11.3942C10.7564 11.138 10.387 11.0752 10.1083 11.254C9.30189 11.7712 8.35563 12.0512 7.39001 12.0512C4.61577 12.0512 2.35876 9.79423 2.35876 7.01999C2.35876 4.24575 4.61577 1.98874 7.39001 1.98874C10.1282 1.98874 12.4213 3.56426 12.4213 6.36374C12.4213 7.42429 11.8445 8.54418 10.8307 8.65222C10.3563 8.6398 10.3683 8.30066 10.4622 7.83125L11.1029 4.51965C11.1813 4.11472 10.8711 3.73874 10.4587 3.73874H9.22872C9.1379 3.73874 9.05027 3.77217 8.98253 3.83265C8.91479 3.89313 8.87168 3.97644 8.86144 4.06667L8.86116 4.06919C8.45929 3.57971 7.75516 3.47378 7.22133 3.47378C5.18203 3.47378 3.45251 5.17549 3.45251 7.61526C3.45251 9.40089 4.45835 10.5101 6.07751 10.5101C6.81536 10.5101 7.6462 10.0826 8.12805 9.46198C8.38842 10.3945 9.23856 10.3945 10.0615 10.3945C13.0395 10.3945 14.1713 8.43634 14.1713 6.36374C14.1713 2.6355 11.1641 0.238739 7.39001 0.238739ZM6.7972 8.563C6.18883 8.563 5.81091 8.13581 5.81091 7.44816C5.81091 6.21789 6.65253 5.45948 7.41408 5.45948C8.02362 5.45948 8.38754 5.87623 8.38754 6.57429C8.38754 7.80642 7.46127 8.563 6.7972 8.563Z" fill="#F07D00"/>
    </svg>
  )
}
function IconLocation() {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" className="h-4 w-4" aria-hidden="true">
      <path d="M6.85047 13.7375C2.87748 7.97787 2.14001 7.38675 2.14001 5.26999C2.14001 2.37049 4.49051 0.019989 7.39001 0.019989C10.2895 0.019989 12.64 2.37049 12.64 5.26999C12.64 7.38675 11.9026 7.97787 7.92956 13.7375C7.66884 14.1142 7.11116 14.1141 6.85047 13.7375ZM7.39001 7.45749C8.59814 7.45749 9.57751 6.47812 9.57751 5.26999C9.57751 4.06186 8.59814 3.08249 7.39001 3.08249C6.18189 3.08249 5.20251 4.06186 5.20251 5.26999C5.20251 6.47812 6.18189 7.45749 7.39001 7.45749Z" fill="#F07D00"/>
    </svg>
  )
}
function IconCheckCircle() {
  return (
    <svg width="14" height="15" viewBox="0 0 14 15" fill="none" className="h-4 w-4 shrink-0" aria-hidden="true">
      <path d="M13.7812 7.60999C13.7812 11.3552 10.7452 14.3912 7 14.3912C3.25481 14.3912 0.21875 11.3552 0.21875 7.60999C0.21875 3.86479 3.25481 0.828735 7 0.828735C10.7452 0.828735 13.7812 3.86479 13.7812 7.60999ZM6.21562 11.2006L11.2469 6.16935C11.4177 5.99851 11.4177 5.72149 11.2469 5.55065L10.6282 4.93194C10.4573 4.76107 10.1803 4.76107 10.0094 4.93194L5.90625 9.03509L3.99057 7.11941C3.81973 6.94857 3.54271 6.94857 3.37184 7.11941L2.75313 7.73812C2.58229 7.90896 2.58229 8.18598 2.75313 8.35682L5.59688 11.2006C5.76775 11.3714 6.04475 11.3714 6.21562 11.2006Z" fill="#046FB7"/>
    </svg>
  )
}
function IconAvatar() {
  return (
    <svg width="14" height="15" viewBox="0 0 14 15" fill="none" className="h-4 w-4 shrink-0" aria-hidden="true">
      <path d="M7 0.828735C3.25391 0.828735 0.21875 3.86389 0.21875 7.60999C0.21875 11.3561 3.25391 14.3912 7 14.3912C10.7461 14.3912 13.7812 11.3561 13.7812 7.60999C13.7812 3.86389 10.7461 0.828735 7 0.828735ZM7 3.45374C8.32891 3.45374 9.40625 4.53108 9.40625 5.85999C9.40625 7.18889 8.32891 8.26624 7 8.26624C5.67109 8.26624 4.59375 7.18889 4.59375 5.85999C4.59375 4.53108 5.67109 3.45374 7 3.45374ZM7 12.86C5.39492 12.86 3.95664 12.1326 2.99414 10.9951C3.5082 10.0272 4.51445 9.35999 5.6875 9.35999C5.75313 9.35999 5.81875 9.37092 5.88164 9.39006C6.23711 9.50491 6.60898 9.57874 7 9.57874C7.39102 9.57874 7.76562 9.50491 8.11836 9.39006C8.18125 9.37092 8.24687 9.35999 8.3125 9.35999C9.48555 9.35999 10.4918 10.0272 11.0059 10.9951C10.0434 12.1326 8.60508 12.86 7 12.86Z" fill="#046FB7"/>
    </svg>
  )
}
function IconPlus() {
  return (
    <svg width="14" height="16" viewBox="0 0 14 16" fill="none" className="h-5 w-5 cursor-pointer" aria-hidden="true">
      <path d="M12.1921 6.20374H7.97179V1.98499C7.97179 1.46731 7.5518 1.04749 7.03393 1.04749H6.09607C5.5782 1.04749 5.15821 1.46731 5.15821 1.98499V6.20374H0.937857C0.419984 6.20374 0 6.62356 0 7.14124V8.07874C0 8.59641 0.419984 9.01624 0.937857 9.01624H5.15821V13.235C5.15821 13.7527 5.5782 14.1725 6.09607 14.1725H7.03393C7.5518 14.1725 7.97179 13.7527 7.97179 13.235V9.01624H12.1921C12.71 9.01624 13.13 8.59641 13.13 8.07874V7.14124C13.13 6.62356 12.71 6.20374 12.1921 6.20374Z" fill="#F07D00"/>
    </svg>
  )
}
function SocialTile({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center justify-center w-8 h-8 rounded-[2.8px] bg-[#F07D00] text-white">
      {children}
    </span>
  )
}
// Brand glyphs (simple-icons paths) — legíveis, fill branco sobre o tile laranja.
function IconFacebook() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  )
}
function IconInstagram() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/>
    </svg>
  )
}
function IconLinkedIn() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z"/>
    </svg>
  )
}
// =============================================================================
// Helpers
// =============================================================================
// Único derivado factual mantido: o público-alvo quando o plano não tem
// `lpHolder` cadastrado (PF = titular; PJ = empresa). Nada é inventado.
function deriveAudience(p: SalesLinkPlan): string {
  return p.planType === 'PESSOA JURÍDICA' ? 'Para empresas (2+ vidas)' : 'Somente Titular'
}

// =============================================================================
// Loading / NotFound
// =============================================================================
function FullScreenSpinner() {
  return (
    <div className="lp-root min-h-dvh flex items-center justify-center bg-gray-100">
      <Loader2 size={28} className="animate-spin text-[#56758d]" />
    </div>
  )
}

function NotFoundScreen() {
  return (
    <LpLayout>
      <main className="bg-gray-100 px-4 sm:px-8 min-h-dvh flex items-center justify-center">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-10 text-center max-w-md mx-auto">
          <AlertTriangle size={36} className="mx-auto text-[#f07d00] mb-3" />
          <h1 className="text-xl font-bold text-[#243c4f]">Link de venda não encontrado</h1>
          <p className="text-sm text-[#56758d] mt-2">
            Este consultor pode estar inativo ou o link foi digitado incorretamente.
          </p>
        </div>
      </main>
    </LpLayout>
  )
}
